import { formatTimeRange } from "@/lib/utils/formatting";
import { getTimezoneSuffix } from "@/lib/utils/timezones";
import type { ActivityTimeSlotRow, SignupCounts } from "../types/db-types";

/** Groups a flat slot list by `activity_id`, e.g. for building an event-wide `Map` from `listTimeSlotsForEventPublic`. */
export function groupTimeSlotsByActivity(slots: readonly ActivityTimeSlotRow[]): Map<number, ActivityTimeSlotRow[]> {
  const map = new Map<number, ActivityTimeSlotRow[]>();
  for (const slot of slots) {
    const existing = map.get(slot.activity_id);
    if (existing) existing.push(slot);
    else map.set(slot.activity_id, [slot]);
  }
  return map;
}

/** Keys a flat signup-counts list into a `Record<activityId, SignupCounts>` for O(1) per-card lookup. */
export function keySignupCountsByActivity(counts: readonly SignupCounts[]): Record<number, SignupCounts> {
  const byActivity: Record<number, SignupCounts> = {};
  for (const c of counts) byActivity[c.activity_id] = c;
  return byActivity;
}

/**
 * Formats a time slot into a human-readable label.
 * @param slot - The time slot row
 * @param timeZone - Optional IANA zone (e.g. "America/New_York"). When provided alongside a
 *   start time, the live short name (EST/EDT/PT/...) is appended after the time range.
 * @returns Display label (e.g. "Mon, Jan 15, 2 - 4 PM EST") or "General Admission" if no date/time set
 */
export function formatSlotLabel(slot: ActivityTimeSlotRow, timeZone?: string | null): string {
  const parts: string[] = [];

  if (slot.date) {
    const date = new Date(slot.date + "T00:00:00");
    parts.push(
      date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    );
  }

  if (slot.start_time) {
    const tzSuffix = getTimezoneSuffix(timeZone, slot.date);
    parts.push(`${formatTimeRange(slot.start_time, slot.end_time)}${tzSuffix}`);
  }

  return parts.length > 0 ? parts.join(", ") : "General Admission";
}

/**
 * Single-line slot label that includes the slot's custom name when set
 * (e.g. "Room A, Mon, Jan 15, 2 - 4 PM EST"). Use for review/confirmation summaries and other
 * single-line contexts that should surface a named slot. The picker renders the name and time on
 * separate lines, so it composes them itself rather than calling this.
 *
 * @param slot - The time slot row.
 * @param timeZone - Optional IANA zone, passed through to {@link formatSlotLabel}.
 * @returns "<name>, <time/date label>" when the slot has a name, otherwise the plain label.
 */
export function formatSlotDisplayLabel(slot: ActivityTimeSlotRow, timeZone?: string | null): string {
  const label = formatSlotLabel(slot, timeZone);
  return slot.name ? `${slot.name}, ${label}` : label;
}
